import React, { useState, useEffect, useContext } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import facturasService from '@services/facturas';
import customersService from '@services/customers';
import ccService from '@services/cc';
import Menu from '@components/Menu';
import DocumentPrintModal from '@components/DocumentPrintModal';
import PDFPrint from '@components/PdfPrint';
import useDocumentPreview from '@hooks/useDocumentPreview';
import {
  MdPrint,
  MdSend,
  MdArrowBack,
  MdReceipt,
  MdPerson,
  MdPayment,
  MdAccessTime,
  MdLocalOffer,
  MdBarChart,
  MdCancel,
  MdNote,
  MdDirectionsCar,
  MdLocalShipping,
  MdPlace,
  MdEuro,
} from "react-icons/md";
import UserContext from '@components/context/userContext';

import ClipLoader from "react-spinners/ClipLoader";

import { confirmAlert } from 'react-confirm-alert';

import "./view.css";
import TestEnvironmentAlert from '@components/TestEnvironmentAlert';

const isInCurrentMonth = (epochSeconds) => {
  const inputDate = new Date(epochSeconds * 1000); // converter para ms
  const now = new Date();

  return (
    inputDate.getFullYear() === now.getFullYear() &&
    inputDate.getMonth() === now.getMonth()
  );
}

const InvoiceDetails = () => {
  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmiting, setIsSubmiting] = useState(false);
  const [hasIsp, setHasIsp] = useState(false);
  const [isps, setIsps] = useState([]);
  const [sc, setSc] = useState([]);
  const [redIsp, setRedIsp] = useState([]);
  const [paymentStatus, setPaymentStatus] = useState(0);
  const [paymentDiff, setPaymentDiff] = useState(0);
  const { doctype, serie, doc, posto = '', tpv = '' } = useParams();
  const history = useHistory();
  const profile = useContext(UserContext);

  // Use the generic document preview hook
  const {
    previewUrl,
    isSubmitting,
    printerRef,
    handleSingleDocumentPrint,
    openPrintModal
  } = useDocumentPreview(profile);

  const payments = [
    { value: 1, label: "Dinheiro" },
    { value: 2, label: "Crédito" },
    { value: 3, label: "Cheque" },
    { value: 4, label: "TPA" },
    { value: 8, label: "Frota" },
    { value: 14, label: "Serviços Internos" },
    { value: 16, label: "Nota de Crédito" },
    { value: 104, label: "Transferência" },
  ];

  const handleGoBack = () => {
    if (window.history.length > 1) {
      // Existe histórico anterior — voltar
      history.goBack();
    } else {
      // Tenta fechar a janela
      window.close();

      // Se não fechar, redireciona
      setTimeout(() => {
        if (!window.closed) {
          history.push('/sales'); // Altera esta rota conforme necessário
        }
      }, 100);
    }
  };

  useEffect(() => {
    const fetchInvoiceDetails = async () => {
      try {
        // Get the invoice details directly using the document information
        const invoiceData = await facturasService.getById(profile.idEmpresa, {
          type: doctype,
          posto,
          tpv,
          serie,
          numero: doc
        });

        if (!invoiceData || invoiceData === 'NOK' || Object.keys(invoiceData) === 0) {
          throw new Error('Failed to find invoice');
        }

        if (['facturas', 'notas_credito'].includes(invoiceData.type)) {
          const ccData = await ccService.get(profile.idEmpresa, {
            days: 0,
            customerId: invoiceData.cliente
          });

          if (ccData?.customers?.[invoiceData.cliente]?.items?.[invoiceData.doc]) {
            setPaymentDiff(parseFloat(ccData?.customers?.[invoiceData.cliente]?.items?.[invoiceData.doc]?.diff) || 0);
            setPaymentStatus(parseFloat(ccData?.customers?.[invoiceData.cliente]?.items?.[invoiceData.doc]?.diff) < parseFloat(invoiceData.valor_liquido) ? 2 : 0);
          } else {
            setPaymentStatus(1);
          }
        }

        invoiceData.idcustomer = invoiceData.cliente;
        invoiceData.idposto = invoiceData.armazem;
        invoiceData.anulado = invoiceData.anulado === 'True';
        invoiceData.payment = invoiceData['cod_forma_pag'] ? parseInt(invoiceData['cod_forma_pag']) : 2;
        invoiceData.payment_method = payments.find(p => p.value === invoiceData.payment);

        if (invoiceData.isp.isp) {
          setHasIsp(true);
          setIsps(invoiceData.isp.isp);
          setSc(invoiceData.isp.sobrecusto_biocombustivel);
          setRedIsp(invoiceData.isp.reducao_isp);
        }

        // Fetch customer details
        if (invoiceData.cliente) {
          const customerData = await customersService.getById(profile.idEmpresa, invoiceData.cliente);
          if (customerData && customerData.length > 0) {
            setCustomer(customerData[0]);
          }
        }

        setInvoice(invoiceData);
      } catch (err) {
        console.error('Error fetching invoice details:', err);
        setError(err.message || 'Error fetching invoice details');
      } finally {
        setLoading(false);
      }
    };

    fetchInvoiceDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile.idEmpresa, doctype, serie, doc, posto, tpv]);

  const handlePrint = async ({ selectedPageSize, selectedPageType }) => {
    await handleSingleDocumentPrint(
      invoice,
      selectedPageSize,
      selectedPageType,
      {
        endpoint: 'facturas'
      }
    );
  };

  const handleEmail = async () => {
    let email = prompt("Email:", invoice.email || customer?.email);
    
    if (email) {
      setIsSubmiting(true);
      //post
      const response = await facturasService.email({
        idempresa: profile.idEmpresa,
        type: invoice.type,
        posto: invoice.posto || null,
        tpv: invoice.tpv || null,
        serie: invoice.serie,
        numero: invoice.numero,
        email: email
      });
      if (response === 'OK') {
        setIsSubmiting(false);
        alert('Email enviado com sucesso');
      } else {
        alert('Erro ao enviar email');
      }
    }
  };

  const handleCancel = async () => {
    setIsSubmiting(true);
    confirmAlert({
        title: 'Confirmação',
        message: `Tem a certeza que deseja anular o documento ${invoice.doc}?\nEsta ação não pode ser desfeita.`,
        buttons: [
            {
                label: 'Sim',
                onClick: () => {
                    facturasService.unset(profile.idEmpresa, {
                      type: invoice.type,
                      posto: invoice.posto || '',
                      tpv: invoice.tpv || '',
                      serie: invoice.serie,
                      numero: invoice.numero
                    })
                    .then(response => {
                        setIsSubmiting(false);
                        if (response !== 'NOK') {
                            window.location.reload();
                        }
                    })
                    .catch(error => {
                        setIsSubmiting(false);
                        console.error(error);
                    });
                }
            },
            {
                label: 'Não',
                onClick: () => {
                  setIsSubmiting(false);
                }
            }
        ]
    });
  };

  const handleEmitReceipt = () => {
    if (!invoice) {
      return;
    }

    const targetCustomerId = customer?.idcustomer || invoice?.idcustomer || invoice?.cliente;

    if (!targetCustomerId) {
      alert('Não foi possível identificar o cliente da fatura.');
      return;
    }

    console.log({
      pathname: '/sales/new/recibos',
      state: {
        prefillRecibo: {
          customerId: targetCustomerId,
          document: {
            doc: invoice?.doc,
            numero: invoice?.numero,
            tipo: invoice?.type || doctype,
          }
        }
      }
    });
    

    history.push({
      pathname: '/sales/new/recibos',
      state: {
        prefillRecibo: {
          customerId: targetCustomerId,
          document: {
            doc: invoice?.doc,
            numero: invoice?.numero,
            tipo: invoice?.type || doctype,
          }
        }
      }
    });
  };

  if (loading) {
    return (
      <div>
        <Menu />
        <div className="container">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <Menu />
        <div className="container">
          <div className="alert alert-danger">{error}</div>
        </div>
      </div>
    );
  }

  if (!invoice) {
    return (
      <div>
        <Menu />
        <div className="container">
          <div className="alert alert-warning">Fatura não encontrada</div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Menu />
      <div className="fullContainer" style={{ maxWidth: '100%', backgroundColor: invoice.anulado ? '#910000' : '#262626' }}>
        <div className="col-xs-12">
          <ol
            style={{
              textAlign: 'left',
              marginTop: '75px',
            }}
            className="breadcrumb"
          >
            <li>
              <a href="http://eposgestofinal.com/index.php">Portal</a>
            </li>
            <li>
              <a href="/sales">Faturação</a>
            </li>
            <li className="active">
              <span>
                {doctype === 'guias_transporte' && 'Detalhes da Guia de Transporte'}
                {doctype === 'notas_credito' && 'Detalhes da Nota de Crédito'}
                {doctype === 'recibos' && 'Detalhes do Recibo'}
                {doctype === 'notas_credito_tpv' && 'Detalhes da Nota de Crédito'}
                {doctype === 'vendas_dinheiro' && 'Detalhes da Fatura/Recibo'}
                {!['guias_transporte','notas_credito','recibos','notas_credito_tpv','vendas_dinheiro'].includes(doctype) && 'Detalhes da Fatura'}
              </span>
            </li>
          </ol>
        </div>
      </div>
      <TestEnvironmentAlert />

      <div className="invoice-container">
      {/* Header Bar */}
      <div className="header-bar" style={{ backgroundColor: invoice.anulado ? '#910000' : '#262626' }}>
        <div className="header-content">
          <button className="btn-back" onClick={handleGoBack}>
            <MdArrowBack size={20} /> <span>Voltar</span>
          </button>
          <div className="document-info">
            <MdReceipt size={24} />
            <div>
              <h1>{invoice.doc} {invoice.anulado ? '(anulado)' : ''}</h1>
              <p>
                {new Date(invoice.date_time * 1000).toLocaleString('pt-PT', {
                  year: 'numeric',
                  month: '2-digit',
                  day: '2-digit',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
          </div>
          {invoice.codigo_at && (
            <div style={{
              background: '#eff6ff',
              border: '1px solid #3b82f6',
              borderRadius: '6px',
              padding: '8px 12px',
              margin: '4px 0',
              fontSize: '12px',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}>
                <div style={{
                  background: '#3b82f6',
                  borderRadius: '50%',
                  width: '20px',
                  height: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '12px',
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  ✓
                </div>
                <span style={{
                  color: '#1e40af',
                  fontWeight: '500'
                }}>
                  Comunicado à AT
                </span>
              </div>
              <div style={{
                color: '#1e40af',
                fontSize: '10px',
              }}>
                código:
                <span style={{
                  color: '#1e40af',
                  fontWeight: '500',
                  backgroundColor: 'white',
                  padding: '4px',
                  borderRadius: '6px',
                  marginLeft: '4px'
                }}>
                  {invoice.codigo_at}
                </span>
              </div>
            </div>
          )}
          {['facturas', 'notas_credito'].includes(invoice.type) && !invoice.anulado && (
            <div style={{
              background: paymentStatus === 1 ? '#f0fdf4' : (paymentStatus === 2 ? '#fefce8' : '#ffe4e6'),
              border: paymentStatus === 1 ? '#22c55e' : (paymentStatus === 2 ? '#f59e0b' : '#ef4444'),
              borderRadius: '6px',
              padding: '8px 12px',
              margin: '4px 0',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px'
            }}>
              <div style={{
                background: paymentStatus === 1 ? '#22c55e' : (paymentStatus === 2 ? '#facc15' : '#ef4444'),
                borderRadius: '50%',
                width: '20px',
                height: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: 'white',
                fontWeight: 'bold'
              }}>
                €
              </div>
              <span style={{
                color: paymentStatus === 1 ? '#166534' : (paymentStatus === 2 ? '#991b1b' : '#6b7280'),
                fontWeight: '500'
              }}>
                {['facturas'].includes(invoice.type) && (paymentStatus === 1 ? 'Paga' : (paymentStatus === 2 ? 'Parcialmente Paga' : 'Não Paga'))}
                {['notas_credito'].includes(invoice.type) && (paymentStatus === 1 ? 'Liquidada' : (paymentStatus === 2 ? 'Parcialmente Liquidada' : 'Não Liquidada'))}
                {paymentStatus === 2 && (<div style={{ fontSize: '12px', color: '#6b7280' }}>Faltam {paymentDiff.toFixed(2)} €</div>)}
              </span>
            </div>
          )}
          <div className="header-actions">
            {invoice.anulado || doctype === 'guias_transporte' ? null : <button disabled={!isInCurrentMonth(invoice.date_time) || doctype === 'taloes'} className="btn-danger-action" onClick={handleCancel}>
              { isSubmiting ? <ClipLoader size={15} /> : <MdCancel size={18} />} <span>Anular</span>
            </button>}
            {['facturas', 'notas_credito'].includes(doctype) && paymentStatus !== 1 && (
              <button className="btn-action" onClick={handleEmitReceipt}>
                <MdEuro size={18} /> <span>Emitir Recibo</span>
              </button>
            )}
            <button className="btn-action" onClick={handleEmail}>
              { isSubmiting ? <ClipLoader size={15} /> : <MdSend size={18} />} <span>Enviar Email</span>
            </button>
            <button className="btn-action" onClick={() => {
              openPrintModal();
            }}>
            { isSubmitting ? <ClipLoader size={15} /> : <MdPrint size={18} />} <span>Imprimir</span>
            </button>
          </div>
        </div>
      </div>

      <div className="content-wrapper">
        {/* Main Content */}
        <div className="invoice-content">
          {/* Client and Document Info */}
          <div className="info-cards-container">
            <div className="info-card client-card">
              <div className="card-header">
                <MdPerson size={20} />
                <h2>Informação do Cliente</h2>
              </div>
              <div className="card-content">
                <h3>{customer?.name || 'Consumidor Final'}</h3>
                {!customer?.name && invoice?.nif !== '999999990' ? (
                  <div className="detail-item">
                    <span>Nome: {invoice?.nome_1}</span>
                  </div>
                ) : null}
                {invoice?.nif !== 999999990 ? (
                  <div className="detail-item">
                    <span>NIF: {invoice?.nif}</span>
                  </div>
                ) : null}
                {invoice?.morada_1 && (
                  <div className="detail-item">
                    <span>{invoice?.morada_1}</span>
                  </div>
                )}
                {invoice?.codigo_postal && (
                  <div className="detail-item">
                    <span>{invoice?.codigo_postal} {invoice?.localidade_postal}</span>
                  </div>
                )}
                {invoice?.email  && (
                  <div className="detail-item">
                    <span>Email: {invoice?.email}</span>
                  </div>
                )}
                {invoice?.matricula || invoice?.codigo_matricula ? (
                  <div className="detail-item">
                    <span>Matrícula: {invoice?.matricula || invoice?.codigo_matricula}</span>
                  </div>
                ) : null}
                {invoice?.kilometros_actuais > 0 ? (
                  <div className="detail-item">
                    <span>Km Atuais: {invoice?.kilometros_actuais}</span>
                  </div>
                ) : null}
              </div>
            </div>

            {doctype === 'guias_transporte' ? (
              <>
                {/* Veículo */}
                <div className="info-card payment-card">
                  <div className="card-header">
                    <i className="fas fa-truck" style={{ marginRight: '8px' }}></i>
                    <h2>Veículo</h2>
                  </div>
                  <div className="card-content">
                    {invoice.matricula && (
                      <div className="detail-item">
                        <MdDirectionsCar size={16} />
                        <span>
                          <strong>Matrícula:</strong> {invoice.matricula}
                        </span>
                      </div>
                    )}
                    {invoice.motorista && (
                      <div className="detail-item">
                        <MdPerson size={16} />
                        <span>
                          <strong>Motorista:</strong> {invoice.motorista}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Detalhes de Carga */}
                <div className="info-card payment-card">
                  <div className="card-header">
                    <i className="fas fa-truck-loading" style={{ marginRight: '8px' }}></i>
                    <h2>Detalhes de Carga</h2>
                  </div>
                  <div className="card-content">
                    {invoice.local_carga && (
                      <div className="detail-item">
                        <MdPlace size={16} />
                        <span style={{ whiteSpace: 'pre-line' }}>
                          <strong>Local:</strong><br />
                          {invoice.local_carga}
                        </span>
                      </div>
                    )}
                    {invoice.data_carga && (
                      <div className="detail-item">
                        <MdAccessTime size={16} />
                        <span>
                          <strong>Data e Hora:</strong> {invoice.data_carga} {invoice.hora_carga || ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Detalhes de Descarga */}
                <div className="info-card payment-card">
                  <div className="card-header">
                    <i className="fas fa-box-open" style={{ marginRight: '8px' }}></i>
                    <h2>Detalhes de Descarga</h2>
                  </div>
                  <div className="card-content">
                    {invoice.local_descarga && (
                      <div className="detail-item">
                        <MdPlace size={16} />
                        <span style={{ whiteSpace: 'pre-line' }}>
                          <strong>Local:</strong><br />
                          {invoice.local_descarga}
                        </span>
                      </div>
                    )}
                    {invoice.data_descarga && (
                      <div className="detail-item">
                        <MdAccessTime size={16} />
                        <span>
                          <strong>Data e Hora:</strong> {invoice.data_descarga} {invoice.hora_descarga || ''}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </>
            ) : doctype !== 'notas_credito' ? (
              <div className="info-card payment-card">
                <div className="card-header">
                  <MdPayment size={20} />
                  <h2>Detalhes de Pagamento</h2>
                </div>
                <div className="card-content">
                  <div className="detail-item">
                    <MdAccessTime size={16} />
                    <span>
                      Vencimento: 
                      {invoice.data_vencimento ? invoice?.data_vencimento || '' : new Date(invoice.date_time * 1000).toLocaleString('pt-PT', {
                        year: 'numeric',
                        month: '2-digit',
                        day: '2-digit',
                        hour: '2-digit',
                        minute: '2-digit'
                      }).slice(0, 10)}
                    </span>
                  </div>
                  <div className="detail-item">
                    <MdLocalOffer size={16} />
                    <span>Método: {invoice.payment_method.label}</span>
                  </div>
                </div>
              </div>
            ) : null}
          </div>

          {/* Document Observations */}
          {invoice.obs && (
            <div className="observations-section">
              <div className="section-header">
                <MdNote size={24} />
                <h2>Observações do Documento</h2>
              </div>
              <div className="observations-content">
                <p>{invoice.obs}</p>
              </div>
            </div>
          )}

          {/* Products Table */}
          <div className="products-section">
            <div className="section-header">
              <MdBarChart size={24} />
              <h2>{doctype === 'guias_transporte' ? 'Produtos' : 'Produtos e Serviços'}</h2>
            </div>
            <div className="table-container">
              <table className="products-table">
                <thead>
                  <tr>
                    <th>Código</th>
                    <th>Produto</th>
                    <th className="text-center">Qtd</th>
                    {doctype !== 'guias_transporte' && (
                      <>
                        <th className="text-end">Preço</th>
                        <th className="text-end">Desc %</th>
                        <th className="text-end">Desc €</th>
                        <th className="text-center">IVA</th>
                        <th className="text-end">Total</th>
                      </>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {invoice.lines.map((line, index) => (
                    <tr key={index}>
                      <td>{line.produto}</td>
                      <td>
                        <div className="product-info">
                          <div className="product-name">{line.designacao}</div>
                          {line.obs_ft && (
                            <div className="product-observation">{line.obs_ft}</div>
                          )}
                        </div>
                      </td>
                      <td className="text-center">{line.quantidade % 1 !== 0 ? parseFloat(line.quantidade)?.toFixed(2) : line.quantidade}</td>
                      {doctype !== 'guias_transporte' && (
                        <>
                          <td className="text-end">{parseFloat(line['preco_sem_iva'])?.toFixed(3)} €</td>
                          <td className="text-end">{parseFloat(line['desconto_per'])?.toFixed(2) || '0.00'}</td>
                          <td className="text-end">{parseFloat(line.valor_desconto)?.toFixed(3) || '0.00'}</td>
                          <td className="text-center">{line.taxa_iva}%</td>
                          <td className="text-end">{(parseFloat(line.valor_iliquido) - parseFloat(line.valor_iva || 0))?.toFixed(2)} €</td>
                        </>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Totals */}
          {doctype !== 'guias_transporte' && (
            <div className="totals-section">
              <div className="totals-card">
                <div className="totals-row">
                  <span>Subtotal</span>
                  <span>{(invoice?.lines || []).reduce((subtotal, line) => {
                    // Calcular subtotal sem IVA: valor_iliquido - valor_iva
                    const valorSemIva = parseFloat(line['valor_iliquido']) - parseFloat(line['valor_iva'] || 0);
                    return subtotal + valorSemIva;
                  }, 0).toFixed(2)} €</span>
                </div>
                <div className="totals-row">
                  <span>Descontos</span>
                  <span>{parseFloat(invoice.valor_desconto)?.toFixed(2)} €</span>
                </div>
                <div className="totals-row">
                  <span>IVA</span>
                  <span>{parseFloat(invoice.valor_iva)?.toFixed(2)} €</span>
                </div>
                <div className="totals-row total">
                  <span>Total</span>
                  <span>{parseFloat(invoice.valor_liquido)?.toFixed(2)} €</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
    <DocumentPrintModal
      onPrint={handlePrint}
      isSubmitting={isSubmitting}
    />
    <PDFPrint ref={printerRef} pdfUrl={previewUrl} />
  </div>
  );
};

export default InvoiceDetails;